import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/context/AuthContext";
import { navigateResumePath, storeGatewayLaunchContext, storeLoginResume, consumeGatewayLaunchContext } from "@/lib/loginResume";

const WORKFLOW_TABS = {
  quick: "QUICK_DAILY_FEEDBACK",
  detailed: "DETAILED_ASSESSMENT",
  progress: "QUICK_DAILY_FEEDBACK",
};

const RESOLVE_TIMEOUT_MS = 65000;

function buildInstructorTarget(data) {
  const learnerId = data.academy_learner_id;
  const workflow = WORKFLOW_TABS[data.workflow_mode] || "QUICK_DAILY_FEEDBACK";
  const params = new URLSearchParams({
    learner_id: learnerId,
    workflow,
    launch: "gdi",
  });
  if (data.workflow_mode === "progress") params.set("focus", "history");
  return `/instructor?${params.toString()}`;
}

export default function GatewayLaunch() {
  const [searchParams] = useSearchParams();
  const ctx = searchParams.get("ctx");
  const navigate = useNavigate();
  const [status, setStatus] = useState("opening");
  const [error, setError] = useState("");
  const resolveStarted = useRef(false);

  useEffect(() => {
    if (!ctx) {
      setError("Missing launch context.");
      setStatus("error");
      return;
    }
    if (resolveStarted.current) return;
    resolveStarted.current = true;
    storeGatewayLaunchContext(ctx);

    let cancelled = false;
    const slowTimer = window.setTimeout(() => {
      if (!cancelled) setStatus("connecting");
    }, 4000);

    const hardTimer = window.setTimeout(() => {
      if (!cancelled) {
        setError("Academy is taking too long to respond. Return to GDI and try Start Quick Feedback again.");
        setStatus("error");
      }
    }, RESOLVE_TIMEOUT_MS);

    (async () => {
      try {
        setStatus("opening");
        const { data } = await api.post("/gateway/resolve-launch", { launch_context: ctx });
        if (cancelled) return;
        const target = buildInstructorTarget(data);
        if (data.requires_login) {
          setStatus("login");
          storeLoginResume(target);
          navigate("/login", { replace: true });
          return;
        }
        setStatus("learner");
        consumeGatewayLaunchContext();
        navigateResumePath(navigate, target);
      } catch (err) {
        if (!cancelled) {
          setError(formatApiError(err) || "Launch could not be completed.");
          setStatus("error");
        }
      } finally {
        window.clearTimeout(slowTimer);
        window.clearTimeout(hardTimer);
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(slowTimer);
      window.clearTimeout(hardTimer);
    };
  }, [ctx, navigate]);

  if (error || status === "error") {
    return (
      <div className="p-6 space-y-3" data-testid="gateway-launch-error">
        <p role="alert" className="text-destructive">
          {error || "Unable to open Academy training."}
        </p>
        <p className="text-sm text-muted-foreground">
          Return to GDI, open the student profile again, and tap Start Quick Feedback for a fresh link.
        </p>
      </div>
    );
  }

  const message =
    status === "connecting"
      ? "Connecting to Academy…"
      : status === "login"
        ? "Signing in required…"
        : status === "learner"
          ? "Opening learner…"
          : "Opening Academy training…";

  return (
    <p className="p-6" data-testid="gateway-launch-loading">
      {message}
    </p>
  );
}
